import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { SelectOrgPrompt, useNeedsOrg } from '../components/SelectOrgPrompt';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shield, ChevronLeft, ChevronRight, Search, Filter, Clock } from 'lucide-react';

import { API_BASE as API } from '../config';

const CATEGORY_COLORS = {
  auth: 'bg-blue-100 text-blue-700',
  claim: 'bg-amber-100 text-amber-700',
  company: 'bg-emerald-100 text-emerald-700',
  user: 'bg-purple-100 text-purple-700',
  tenant: 'bg-cyan-100 text-cyan-700',
  document: 'bg-pink-100 text-pink-700',
  note: 'bg-orange-100 text-orange-700',
  organization: 'bg-indigo-100 text-indigo-700',
};

const CATEGORIES = ['auth', 'claim', 'company', 'user', 'tenant', 'document', 'note', 'organization'];

// Phase 5 — quick-filter chips (preset action regex + category combos).
// `action` is sent to the backend as a case-insensitive regex.
const QUICK_FILTERS = [
  { id: 'all',     label: 'All',              category: '', action: '' },
  { id: 'access',  label: 'Access & Members', category: '', action: 'user\\.access_updated|org\\.member_added|org\\.member_removed' },
  { id: 'perms',   label: 'Permissions',      category: 'tenant', action: 'tenant\\.feature_flag_updated' },
  { id: 'claims',  label: 'Claims',           category: 'claim', action: '' },
  { id: 'cos',     label: 'Companies',        category: 'company', action: '' },
  { id: 'orgs',    label: 'Organizations',    category: 'organization', action: '' },
  { id: 'auth',    label: 'Auth',             category: 'auth', action: '' },
];

function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function AuditLog() {
  const { user, operatorContext, activeOrgId, userOrgs } = useAuth();
  const needsOrg = useNeedsOrg();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState('');
  const [searchAction, setSearchAction] = useState('');
  const limit = 25;
  const isOperator = user?.is_platform_operator;
  const inContext = isOperator && operatorContext?.tenantId;
  const isInOrgView = user?.account_type === 'provider' && !!activeOrgId;
  const activeOrg = isInOrgView ? userOrgs.find(o => o.org_id === activeOrgId) : null;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      if (isOperator && !inContext) {
        const res = await axios.get(`${API}/operator/audit-log`, { withCredentials: true });
        let filtered = res.data;
        if (category) filtered = filtered.filter(l => l.category === category);
        if (searchAction) filtered = filtered.filter(l => l.action?.toLowerCase().includes(searchAction.toLowerCase()));
        setTotal(filtered.length);
        setLogs(filtered.slice((page - 1) * limit, page * limit));
      } else {
        const params = new URLSearchParams({ page, limit });
        if (category) params.append('category', category);
        if (searchAction) params.append('action', searchAction);
        if (isInOrgView) params.append('org_id', activeOrgId);
        const res = await axios.get(`${API}/audit-logs?${params}`, { withCredentials: true });
        setLogs(res.data.logs);
        setTotal(res.data.total);
      }
    } catch (err) {
      console.error('Failed to fetch audit logs', err);
    } finally {
      setLoading(false);
    }
  }, [page, category, searchAction, activeOrgId]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / limit);

  if (needsOrg && !isOperator) return <SelectOrgPrompt />;

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="audit-log-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <Shield className="w-5 md:w-6 h-5 md:h-6 text-slate-700" />
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900" data-testid="audit-log-title">
              {isInOrgView ? `${activeOrg?.name || 'Organization'} — Audit Log` : isOperator && !inContext ? 'All About Claims — Audit Log' : inContext ? `${operatorContext.tenantName} — Audit Log` : 'Audit Log'}
            </h1>
            <p className="text-sm text-gray-500">{total} total events</p>
          </div>
        </div>

        {/* Phase 5 — Quick filter chips */}
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="audit-quick-filters">
          {QUICK_FILTERS.map((f) => {
            const active = (category || '') === f.category && (searchAction || '') === f.action;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => { setCategory(f.category); setSearchAction(f.action); setPage(1); }}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
                data-testid={`audit-quick-${f.id}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-4 bg-white rounded-lg border p-3" data-testid="audit-log-filters">
          <Filter className="w-4 h-4 text-gray-400" />
          <Select value={category} onValueChange={(v) => { setCategory(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-40 h-9" data-testid="audit-filter-category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
            <Input
              placeholder="Search actions..."
              value={searchAction}
              onChange={(e) => { setSearchAction(e.target.value); setPage(1); }}
              className="pl-8 w-52 h-9"
              data-testid="audit-filter-search"
            />
          </div>
          {(category || searchAction) && (
            <Button variant="ghost" size="sm" onClick={() => { setCategory(''); setSearchAction(''); setPage(1); }} data-testid="audit-filter-clear">
              Clear
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-x-auto">
          <table className="w-full" data-testid="audit-log-table">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Time</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Category</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Action</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">Description</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-3">User</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={5} className="text-center py-12 text-gray-400">Loading...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-gray-400">No audit logs found</td></tr>
              ) : logs.map((log) => (
                <tr key={log.log_id} className="hover:bg-gray-50 transition-colors" data-testid={`audit-row-${log.log_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatDate(log.created_at)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${CATEGORY_COLORS[log.category] || 'bg-gray-100 text-gray-600'}`}>
                      {log.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-700">{log.action}</code>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{log.description}</td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-xs text-gray-700">{log.user_name || log.user_email || '-'}</p>
                      {log.user_email && log.user_name && <p className="text-[10px] text-gray-400">{log.user_email}</p>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4" data-testid="audit-pagination">
            <p className="text-xs text-gray-500">Page {page} of {totalPages} ({total} events)</p>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="audit-prev-page">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="audit-next-page">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
